import { collection, query, orderBy, limit, startAfter, where, getCountFromServer, getDocs } from "firebase/firestore";
import getTracks from "./getTrackData";

const dlcCups = ["golden-dash", "lucky-cat", "turnip", "propeller", "rock", "moon", "fruit", "boomerang", "feather", "cherry", "acorn", "spiny"];

/**
 * Check if a cup contains DLC tracks
 */
export function isDLCCup(cup) {
    return cup.tracks.some(track => dlcCups.includes(track.cup.name));
}

/**
 * Build Firestore query with filters and pagination
 */
export function buildCupsQuery(firestore, filters, pagination) {
    const { excludeDLC, selectedTracks, sortOrder = 'newest' } = filters;
    const { cupsPerPage, lastVisible, pageNum } = pagination;
    
    const constraints = [orderBy("timestamp", sortOrder === 'oldest' ? 'asc' : 'desc')];
    
    // Add DLC filter if needed
    if (excludeDLC) {
        constraints.push(where("containsDLC", "==", false));
    }
    
    // Add track filter - Firestore only allows ONE array-contains per query
    // If multiple tracks selected, we'll use array-contains-any and filter client-side
    if (selectedTracks && selectedTracks.length > 0) {
        const trackNames = selectedTracks.map(t => t.name);
        if (trackNames.length === 1) {
            constraints.push(where("trackList", "array-contains", trackNames[0]));
        } else {
            // For multiple tracks, we need to get cups with ANY of these tracks
            // and filter for ALL of them client-side in loadCupsPage
            constraints.push(where("trackList", "array-contains-any", trackNames));
        }
    }
    
    // Add pagination - need more results when filtering multiple tracks client-side
    const limitCount = (selectedTracks && selectedTracks.length > 1) 
        ? cupsPerPage * 3  // Fetch more to account for client-side filtering
        : cupsPerPage;
    
    if (pageNum > 1 && lastVisible) {
        constraints.push(startAfter(lastVisible));
    }
    constraints.push(limit(limitCount));
    
    return query(collection(firestore, "cups"), ...constraints);
}

/**
 * Get total count of cups matching filters
 */
export async function fetchCupsCount(firestore, filters) {
    const { excludeDLC, selectedTracks, sortOrder = 'newest' } = filters;
    
    try {
        const constraints = [];
        
        // Only add orderBy if we have other constraints, otherwise it's not needed for count
        if (excludeDLC || (selectedTracks && selectedTracks.length > 0)) {
            constraints.push(orderBy("timestamp", sortOrder === 'oldest' ? 'asc' : 'desc'));
        }
        
        if (excludeDLC) {
            constraints.push(where("containsDLC", "==", false));
        }
        
        // For count, we can't do client-side filtering, so this will be approximate
        // when multiple tracks are selected (it counts cups with ANY of the tracks)
        if (selectedTracks && selectedTracks.length > 0) {
            const trackNames = selectedTracks.map(t => t.name);
            if (trackNames.length === 1) {
                constraints.push(where("trackList", "array-contains", trackNames[0]));
            } else {
                constraints.push(where("trackList", "array-contains-any", trackNames));
            }
        }
        
        const countQuery = constraints.length > 0 
            ? query(collection(firestore, "cups"), ...constraints)
            : collection(firestore, "cups");
            
        const snapshot = await getCountFromServer(countQuery);
        const count = snapshot.data().count;
        console.log("Fetched count:", count);
        return count;
    } catch (error) {
        console.error("Error fetching count:", error);
        // Fallback: try to get count without any constraints
        try {
            const fallbackSnapshot = await getCountFromServer(collection(firestore, "cups"));
            return fallbackSnapshot.data().count;
        } catch (fallbackError) {
            console.error("Fallback count also failed:", fallbackError);
            return 0;
        }
    }
}

/**
 * Load cups from Firestore for a specific page
 */
export async function loadCupsPage(firestore, filters, pagination) {
    try {
        const { selectedTracks } = filters;
        const { cupsPerPage } = pagination;
        
        const q = buildCupsQuery(firestore, filters, pagination);
        const response = await getDocs(q);
        
        let loadedCups = [];
        response.forEach((doc) => {
            const docData = doc.data();
            loadedCups.push({ id: doc.id, ...docData, tracks: getTracks(docData.tracks) });
        });

        const beforeFilterCount = loadedCups.length;
        
        // Client-side filtering for multiple track selection (AND logic)
        if (selectedTracks && selectedTracks.length > 1) {
            const trackNames = selectedTracks.map(t => t.name);
            loadedCups = loadedCups.filter(cup => {
                // Cup must contain ALL selected tracks
                return trackNames.every(trackName => cup.trackList.includes(trackName));
            });
        }
        
        const afterFilterCount = loadedCups.length;
        
        // Limit to requested page size after client-side filtering
        loadedCups = loadedCups.slice(0, cupsPerPage);

        const newLastVisible = response.docs.length > 0 
            ? response.docs[response.docs.length - 1] 
            : null;

        return {
            cups: loadedCups,
            lastVisible: newLastVisible,
            clientFiltered: selectedTracks && selectedTracks.length > 1,
            filteredCount: afterFilterCount,
            fetchedCount: beforeFilterCount
        };
    } catch (error) {
        console.error("Error loading cups:", error);
        return {
            cups: [],
            lastVisible: null,
            clientFiltered: false,
            filteredCount: 0,
            fetchedCount: 0
        };
    }
}
